import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import api from "../../../services/api";

/**
 * Map API / stored role objects to wizard role type. Backend may use
 * `role_type`, camelCase, or omit the field — defaulting only to action_based
 * when we truly cannot tell, so access-based roles still open the screen/data wizard after edit.
 */
function resolveWizardRoleType(role) {
  if (!role || typeof role !== "object") return "action_based";
  if (role.accessBased === true || role.access_based === true) {
    return "access_based";
  }
  const raw = role.roleType ?? role.role_type ?? role.RoleType;
  if (raw == null || raw === "") return "action_based";
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, "_");
  if (s === "access_based" || s === "accessbased" || s === "access") {
    return "access_based";
  }
  if (s === "action_based" || s === "actionbased" || s === "action") {
    return "action_based";
  }
  return "action_based";
}

/**
 * Maps API / legacy role shapes to wizard values. Lists often send `role_type`
 * while the UI expects `roleType`; missing mapping made edits look "action_based"
 * so Next never advanced to Screen Access (step 3).
 */
function normalizeRoleWizardType(role) {
  if (!role || typeof role !== "object") return "action_based";
  if (
    role.accessBased === true ||
    role.access_based === true ||
    role.use_access_based === true
  ) {
    return "access_based";
  }
  const raw =
    role.roleType ??
    role.role_type ??
    role.RoleType ??
    role.roletype ??
    role.permissionMode ??
    role.permission_mode;
  if (raw == null || raw === "") return "action_based";
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
  if (s === "access_based" || s === "accessbased" || s === "access") {
    return "access_based";
  }
  return "action_based";
}

/**
 * Confirmation dialog for role deletion — matches RoleFormPopup overlay + panel styling.
 */
export function DeleteRoleConfirmModal({
  open,
  roleName,
  onConfirm,
  onCancel,
  loading = false,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
      role="presentation"
    >
      <div
        className="relative w-full max-w-md border border-gray-300 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-role-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 border-b border-gray-300 p-4">
          <h3
            id="delete-role-title"
            className="text-xl font-bold text-gray-900"
          >
            Delete role
          </h3>
        </div>
        <div className="p-4">
          <p className="text-sm leading-relaxed text-gray-700">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-gray-900">
              {roleName?.trim() ? roleName : "this role"}
            </span>
            ? This cannot be undone.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-300 p-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-md border-2 border-gray-500 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-600 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="rounded-md border-2 border-redhalo-red bg-redhalo-red px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 disabled:pointer-events-none disabled:opacity-50"
          >
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Encapsulates the role CRUD + wizard bookkeeping. Returns handler functions
 * that close over the shared state/setters passed in. All legacy behaviour
 * (localStorage fallback, wizard progression, filter integration) is preserved.
 */
export default function useRoleCrud({
  roles,
  fetchRoles,
  formData,
  setFormData,
  filterResults,
  setFilterResults,
  editingRole,
  setEditingRole,
  roleAssignmentType,
  setRoleAssignmentType,
  wizardRoleType,
  setWizardRoleType,
  setWizardRoleId,
  setWizardStep,
  setShouldFetchAccess,
  setFetchFromRoleId,
  setSelectedRoleForAccess,
  setSelectedModuleForAccess,
  setShowRoleForm,
  setPendingRoleDraft,
  canCreateRole,
  canEditRole,
  canDeleteRole,
  canEditScreenAccess,
  canEditDataAccess,
}) {
  const [deleteConfirmRoleId, setDeleteConfirmRoleId] = useState(null);
  const [deleteRoleInProgress, setDeleteRoleInProgress] = useState(false);

  const resetRoleForm = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      roleName: "",
      roleDescription: "",
      inheritFromRole: "",
      assignmentType: null,
      filterData: null,
    }));
    setEditingRole(null);
    setRoleAssignmentType(null);
    setFilterResults(null);
  }, [setFormData, setEditingRole, setRoleAssignmentType, setFilterResults]);

  const resetWizard = useCallback(() => {
    setWizardStep(1);
    setWizardRoleType(null);
    setWizardRoleId(null);
    setShouldFetchAccess(null);
    setFetchFromRoleId(null);
    setSelectedRoleForAccess(null);
    setSelectedModuleForAccess(null);
    setPendingRoleDraft(null);
  }, [
    setWizardStep,
    setWizardRoleType,
    setWizardRoleId,
    setShouldFetchAccess,
    setFetchFromRoleId,
    setSelectedRoleForAccess,
    setSelectedModuleForAccess,
    setPendingRoleDraft,
  ]);

  const handleEditRole = useCallback(
    (role) => {
      if (!canEditRole) {
        toast.error("You do not have permission to edit roles.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        roleName: role.name || "",
        roleDescription: role.description || "",
        inheritFromRole: role.inheritFromRole || "",
        assignmentType: role.assignmentType || null,
        filterData: role.filterData || null,
      }));
      setEditingRole(role);
      setRoleAssignmentType(role.assignmentType || null);
      if (role.filterData) setFilterResults(role.filterData);
      // Ensure edit mode knows the role type so the same wizard path can be used
      // when the role is access-based (screen/data access).
      setWizardRoleType(normalizeRoleWizardType(role));
      setWizardRoleId(role.id);
      setSelectedRoleForAccess(role.id);
      // Fetch-access (wizard step 2) is part of create flow; keep edit state
      // deterministic in case user navigates backward.
      setShouldFetchAccess(false);
      setFetchFromRoleId(null);
      if (role.type === "system") {
        // System roles should skip the role details popup and go straight
        // to screen access configuration.
        setShowRoleForm(false);
        setWizardStep(3);
      } else {
        setShowRoleForm(true);
      }
    },
    [
      canEditRole,
      setFormData,
      setEditingRole,
      setRoleAssignmentType,
      setFilterResults,
      setShowRoleForm,
      setWizardRoleType,
      setWizardRoleId,
      setSelectedRoleForAccess,
      setShouldFetchAccess,
      setFetchFromRoleId,
    ]
  );

  const handleDeactivateRole = useCallback(
    async (roleId) => {
      if (!canEditRole) {
        toast.error("You do not have permission to edit roles.");
        return;
      }
      try {
        const role = roles.find((r) => r.id === roleId);
        const payload = { ...role, active: false };
        try {
          await api.put(`/global-settings/roles/${roleId}`, payload);
        } catch (apiError) {
          const stored = localStorage.getItem("roles");
          if (stored) {
            const currentList = JSON.parse(stored);
            const updatedList = currentList.map((r) =>
              r.id === roleId ? payload : r
            );
            localStorage.setItem("roles", JSON.stringify(updatedList));
          }
        }
        await fetchRoles();
      } catch (error) {
        console.error("Error deactivating role:", error);
        toast.error("Failed to deactivate role. Please try again.");
      }
    },
    [canEditRole, roles, fetchRoles]
  );

  const handleReactivateRole = useCallback(
    async (roleId) => {
      if (!canEditRole) {
        toast.error("You do not have permission to edit roles.");
        return;
      }
      try {
        const role = roles.find((r) => r.id === roleId);
        const payload = { ...role, active: true };
        try {
          await api.put(`/global-settings/roles/${roleId}`, payload);
        } catch (apiError) {
          const stored = localStorage.getItem("roles");
          if (stored) {
            const currentList = JSON.parse(stored);
            const updatedList = currentList.map((r) =>
              r.id === roleId ? payload : r
            );
            localStorage.setItem("roles", JSON.stringify(updatedList));
          }
        }
        await fetchRoles();
      } catch (error) {
        console.error("Error reactivating role:", error);
        toast.error("Failed to reactivate role. Please try again.");
      }
    },
    [canEditRole, roles, fetchRoles]
  );

  /** Opens the delete confirmation modal (see DeleteRoleConfirmModal). */
  const handleDeleteRole = useCallback(
    (roleId) => {
      if (!canDeleteRole) {
        toast.error("You do not have permission to delete roles.");
        return;
      }
      const role = roles.find(
        (r) => String(r.id) === String(roleId)
      );
      if (role && role.deletable === false) {
        toast.error("This role cannot be deleted as it is a system role.");
        return;
      }
      setDeleteConfirmRoleId(roleId);
    },
    [canDeleteRole, roles]
  );

  const cancelDeleteRole = useCallback(() => {
    if (deleteRoleInProgress) return;
    setDeleteConfirmRoleId(null);
  }, [deleteRoleInProgress]);

  const confirmDeleteRole = useCallback(async () => {
    const roleId = deleteConfirmRoleId;
    if (roleId == null) return;

    setDeleteRoleInProgress(true);
    try {
      try {
        await api.delete(`/global-settings/roles/${roleId}`);
      } catch (apiError) {
        const stored = localStorage.getItem("roles");
        if (stored) {
          const currentList = JSON.parse(stored);
          const updatedList = currentList.filter(
            (r) => String(r.id) !== String(roleId)
          );
          localStorage.setItem("roles", JSON.stringify(updatedList));
        }
      }
      await fetchRoles();
      setDeleteConfirmRoleId(null);
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role. Please try again.");
    } finally {
      setDeleteRoleInProgress(false);
    }
  }, [deleteConfirmRoleId, fetchRoles]);

  const handleRoleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        if (editingRole && !canEditRole) {
          toast.error("You do not have permission to edit roles.");
          return;
        }
        if (!editingRole && !canCreateRole) {
          toast.error("You do not have permission to create roles.");
          return;
        }
        if (!editingRole && !(formData.roleName || "").trim()) {
          toast.error("Please enter a role name.");
          return;
        }
        if (!editingRole && !wizardRoleType) {
          toast.error("Please select a role type (Action Based or Access Based).");
          return;
        }
        // Access-based wizard uses screen + data steps; allow continuing if the user can
        // edit at least one—remaining steps are read-only via canEdit* flags on the views.
        if (
          !editingRole &&
          wizardRoleType === "access_based" &&
          !canEditScreenAccess &&
          !canEditDataAccess
        ) {
          toast.error(
            "You do not have permission to configure screen or data access for an access-based role."
          );
          return;
        }
        const effectiveWizardType = editingRole
          ? normalizeRoleWizardType(editingRole)
          : wizardRoleType || "action_based";

        // New roles are always custom; system roles must stay system on edit (immutable).
        const isSystemEdit = editingRole?.type === "system";
        const roleKind =
          editingRole?.type === "system" ? "system" : "custom";
        const code = isSystemEdit
          ? editingRole.code ||
            String(formData.roleName || "")
              .trim()
              .toUpperCase()
              .replace(/\s+/g, "_")
          : formData.roleName.toUpperCase().replace(/\s+/g, "_");
        const payload = {
          name: formData.roleName,
          code,
          type: editingRole ? roleKind : "custom",
          level: isSystemEdit ? editingRole.level : "group",
          description: formData.roleDescription,
          inheritFromRole: isSystemEdit
            ? editingRole.inheritFromRole ?? null
            : formData.inheritFromRole || null,
          active: editingRole ? editingRole.active : true,
          assignmentType: isSystemEdit
            ? editingRole.assignmentType ?? null
            : roleAssignmentType || null,
          filterData: isSystemEdit
            ? editingRole.filterData ?? null
            : roleAssignmentType === "filter"
              ? filterResults
              : null,
          roleType: isSystemEdit ? normalizeRoleWizardType(editingRole) : effectiveWizardType,
        };

        if (!editingRole && wizardRoleType === "access_based") {
          const draftRoleId = `draft_${Date.now()}`;
          setPendingRoleDraft(payload);
          setWizardRoleId(draftRoleId);
          setSelectedRoleForAccess(draftRoleId);
          setWizardStep(2);
          setShowRoleForm(false);
          return;
        }

        let roleId;
        try {
          if (editingRole) {
            await api.put(`/global-settings/roles/${editingRole.id}`, payload);
            roleId = editingRole.id;
          } else {
            const response = await api.post("/global-settings/roles", payload);
            roleId = response.data?.id || Date.now().toString();
          }
        } catch (apiError) {
          const stored = localStorage.getItem("roles");
          const currentList = stored ? JSON.parse(stored) : [];
          roleId = editingRole ? editingRole.id : Date.now().toString();
          const roleData = {
            id: roleId,
            ...payload,
            deletable: true,
            createdAt: editingRole
              ? editingRole.createdAt
              : new Date().toISOString(),
          };
          if (editingRole) {
            const updatedList = currentList.map((r) =>
              r.id === editingRole.id ? roleData : r
            );
            localStorage.setItem("roles", JSON.stringify(updatedList));
          } else {
            currentList.push(roleData);
            localStorage.setItem("roles", JSON.stringify(currentList));
          }
        }

        await fetchRoles();

        if (editingRole) {
          // Anyone who can edit screen access should reach the Screen Access step
          // after saving role details — otherwise mismatched/missing API `roleType`
          // (still showing Action Based in the immutable radios) stranded users who
          // only wanted permission changes.
          if (canEditScreenAccess) {
            setWizardRoleId(roleId);
            setSelectedRoleForAccess(roleId);
            setWizardStep(3);
            setShowRoleForm(false);
            return;
          }
          resetRoleForm();
          setShowRoleForm(false);
          return;
        }

        if (effectiveWizardType === "action_based" || effectiveWizardType === "access_based") {
          setWizardRoleId(roleId);
          setSelectedRoleForAccess(roleId);
          setWizardStep(2);
          setShowRoleForm(false);
          return;
        }

        resetRoleForm();
        setShowRoleForm(false);
      } catch (error) {
        console.error("Error saving role:", error);
        toast.error("Failed to save role. Please try again.");
      }
    },
    [
      formData,
      editingRole,
      roleAssignmentType,
      filterResults,
      wizardRoleType,
      canCreateRole,
      canEditRole,
      canEditScreenAccess,
      canEditDataAccess,
      fetchRoles,
      resetRoleForm,
      resetWizard,
      setShowRoleForm,
      setWizardRoleId,
      setWizardStep,
      setPendingRoleDraft,
      setSelectedRoleForAccess,
    ]
  );

  return {
    resetRoleForm,
    resetWizard,
    handleEditRole,
    handleDeactivateRole,
    handleReactivateRole,
    handleDeleteRole,
    confirmDeleteRole,
    cancelDeleteRole,
    deleteConfirmRoleId,
    deleteRoleInProgress,
    handleRoleSubmit,
  };
}
